import { useState } from 'react'
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from '@tanstack/react-query'
import { IPaginatedData } from 'models/shared.models'
import { Accountant } from 'models/management.models'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import useDataGridServerPagination, {
  IUseDataGridServerPagination,
} from 'hooks/Shared/useDataGridServerPagination'
import useUpdateStateFunction from 'hooks/Shared/useUpdateStateFunction'
import { useAppSelector } from 'store/hooks'
import { IApiError } from 'services/Shared/shared.models'
import { getManagementAccountants } from 'services/Management/management.services'

interface IManagementAccountantsState {
  param: string
}

export interface IUseManagementAccountantsContainer
  extends IManagementAccountantsState {
  managementAccountantsPaginatedData: Omit<
    IUseDataGridServerPagination<Accountant>,
    'refetch'
  >
  refetchManagementAccountants: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<IPaginatedData<Accountant>, IApiError>>
  updateManagementAccountantsState: (
    update:
      | Partial<IManagementAccountantsState>
      | ((
          prevState: IManagementAccountantsState
        ) => IManagementAccountantsState)
  ) => void
}

const useManagementAccountantsContainer =
  (): IUseManagementAccountantsContainer => {
    const activeWorkspace = useAppSelector(selectActiveWorkspace)

    const [managementAccountantsState, setManagementAccountantsState] =
      useState<IManagementAccountantsState>({
        param: '',
      })

    const { param } = managementAccountantsState

    const updateManagementAccountantsState =
      useUpdateStateFunction<IManagementAccountantsState>(
        setManagementAccountantsState
      )

    const {
      refetch: refetchManagementAccountants,
      ...managementAccountantsPaginatedData
    } = useDataGridServerPagination<Accountant, { param: string }>({
      activeWorkspace,
      fetchFunction: getManagementAccountants,
      errorMessage: 'No se ha podido obtener la lista de contadores',
      withFilters: true,
      filters: {
        param,
      },
      pageSize: 10,
    })

    return {
      ...managementAccountantsState,
      managementAccountantsPaginatedData,
      refetchManagementAccountants,
      updateManagementAccountantsState,
    }
  }

export default useManagementAccountantsContainer
